import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import { UploadCloud, FileText, CheckCircle2, Loader2, ArrowRight, Lock, Database, Check, Pencil, Trash2, Eye, X } from "lucide-react";

const STEPS = [
  { n: 1, t: "Open your account's data export", d: "In the account you keep your contacts in, go to Settings & Privacy → Data Privacy → Get a copy of your data." },
  { n: 2, t: "Choose the full archive", d: "Select \"Download larger data archive, including connections, contacts and more\", then click Request archive." },
  { n: 3, t: "Download the ZIP from the FIRST email", d: "You'll receive an email with a link (often within minutes) to a .zip file. That first ZIP already contains all your contacts. You can ignore the second email that arrives later." },
  { n: 4, t: "Upload the ZIP here", d: "Just drop the whole .zip below, no need to unzip. We automatically pull out your connections and ignore everything else." },
];

export default function UploadPage() {
  const [phase, setPhase] = useState("idle"); // idle | uploading | processing | done
  const [progress, setProgress] = useState({ enriched: 0, limit: 0, total: 0, locked: 0 });
  const [dragOver, setDragOver] = useState(false);
  const [datasets, setDatasets] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const inputRef = useRef();
  const navigate = useNavigate();

  const loadDatasets = useCallback(async () => {
    try {
      const { data } = await api.get("/uploads");
      setDatasets(data.uploads || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadDatasets(); }, [loadDatasets]);

  const activate = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/uploads/${id}/activate`);
      await loadDatasets();
      toast.success("Now viewing this dataset");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not switch dataset"); }
    finally { setBusyId(null); }
  };

  const saveRename = async (id) => {
    const label = editValue.trim();
    if (!label) { setEditingId(null); return; }
    try {
      await api.patch(`/uploads/${id}`, { label });
      setEditingId(null);
      await loadDatasets();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Rename failed"); }
  };

  const doDelete = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/uploads/${id}`);
      setConfirmId(null);
      await loadDatasets();
      toast.success("Dataset deleted");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Delete failed"); }
    finally { setBusyId(null); }
  };

  const poll = async (uploadId) => {
    try {
      const { data } = await api.get(`/uploads/${uploadId}/status`);
      setProgress((p) => ({ ...p, enriched: data.enriched_count, limit: data.enrich_limit, total: data.total_contacts, createdAt: p.createdAt || data.created_at }));
      if (data.status === "completed") { setPhase("done"); loadDatasets(); return; }
      setTimeout(() => poll(uploadId), 2000);
    } catch { setTimeout(() => poll(uploadId), 3000); }
  };

  const fmtEta = (sec) => {
    if (sec == null) return null;
    if (sec < 60) return `about ${Math.max(5, Math.round(sec / 5) * 5)}s left`;
    const m = Math.floor(sec / 60);
    const s = Math.round((sec % 60) / 15) * 15;
    return `about ${m}m${s ? ` ${s}s` : ""} left`;
  };

  const handleFile = async (file) => {
    if (!file) return;
    const ok = /\.(zip|csv|xlsx|xls)$/i.test(file.name);
    if (!ok) { toast.error("Please upload your contacts .zip (or a Connections.csv / .xlsx)"); return; }
    setPhase("uploading");
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      setProgress({ enriched: 0, limit: data.enrich_limit, total: data.total_contacts, locked: data.locked });
      setPhase("processing");
      poll(data.upload_id);
      loadDatasets();
    } catch (err) {
      setPhase("idle");
      toast.error(apiError(err.response?.data?.detail) || "Upload failed");
    }
  };

  const pct = progress.limit ? Math.round((progress.enriched / progress.limit) * 100) : 0;
  const elapsedSec = progress.createdAt ? (Date.now() - new Date(progress.createdAt).getTime()) / 1000 : 0;
  const rate = progress.enriched > 0 && elapsedSec > 3 ? progress.enriched / elapsedSec : 0;
  const remainingCount = Math.max(0, (progress.limit || 0) - progress.enriched);
  const etaSec = rate > 0 && remainingCount > 0 ? Math.round(remainingCount / rate) : null;
  const etaLabel = fmtEta(etaSec);

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto animate-fade-up">
        <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Import your connections</h1>
        <p className="text-slate-400 mb-8">Follow the guide, then drop your contacts export. Everything is categorized from your uploaded file only (CSV, Excel or ZIP), never scraped.</p>

        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-xl border border-[#27334D] bg-[#111726] p-5 flex gap-4">
              <div className="w-8 h-8 rounded-lg bg-blue-600/15 border border-blue-500/25 text-blue-400 font-mono-niq flex items-center justify-center text-sm shrink-0">{s.n}</div>
              <div>
                <div className="font-medium text-sm mb-1">{s.t}</div>
                <div className="text-xs text-slate-400 leading-relaxed">{s.d}</div>
              </div>
            </div>
          ))}
        </div>

        {datasets.length > 0 && (
          <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-5 sm:p-6 mb-8" data-testid="datasets-manager">
            <div className="flex items-center gap-2.5 mb-1">
              <Database className="w-4.5 h-4.5 text-blue-400" />
              <h2 className="font-display text-lg font-semibold">Your datasets</h2>
            </div>
            <p className="text-xs text-slate-500 mb-5 font-mono-niq">Each upload is kept separately. Pick which one to view, rename it, or delete the ones you no longer need.</p>
            <div className="space-y-2.5">
              {datasets.map((d) => (
                <div key={d.id} data-testid={`dataset-row-${d.id}`} className={`rounded-xl border px-4 py-3 flex items-center gap-3 transition-colors ${d.is_active ? "border-blue-500/60 bg-blue-600/[0.08]" : "border-[#27334D] bg-[#0D121C]"}`}>
                  <div className="min-w-0 flex-1">
                    {editingId === d.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          autoFocus
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveRename(d.id); if (e.key === "Escape") setEditingId(null); }}
                          data-testid={`dataset-rename-input-${d.id}`}
                          className="bg-[#111726] border border-blue-500/40 rounded-lg px-2.5 py-1.5 text-sm w-full max-w-xs outline-none focus:border-blue-500"
                          placeholder="Dataset name"
                        />
                        <button onClick={() => saveRename(d.id)} data-testid={`dataset-rename-save-${d.id}`} className="p-1.5 rounded-md bg-blue-600 hover:bg-blue-500"><Check className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setEditingId(null)} className="p-1.5 rounded-md border border-[#27334D] hover:border-slate-500"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate" data-testid={`dataset-name-${d.id}`}>{d.display_name}</span>
                          {d.is_active && <span className="text-[10px] uppercase tracking-wider font-mono-niq text-blue-300 bg-blue-600/20 border border-blue-500/30 px-2 py-0.5 rounded-full">Viewing</span>}
                          {d.status === "processing" && <span className="text-[10px] uppercase tracking-wider font-mono-niq text-amber-300 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Enriching</span>}
                        </div>
                        <div className="text-xs text-slate-500 font-mono-niq mt-0.5">
                          {(d.enriched_count ?? 0).toLocaleString()} / {(d.total_contacts ?? 0).toLocaleString()} enriched · {d.created_at ? new Date(d.created_at).toLocaleDateString() : ""}
                        </div>
                      </>
                    )}
                  </div>

                  {editingId !== d.id && confirmId !== d.id && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      {!d.is_active && (
                        <button onClick={() => activate(d.id)} disabled={busyId === d.id} data-testid={`dataset-view-${d.id}`} className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-500 px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50">
                          {busyId === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} View
                        </button>
                      )}
                      <button onClick={() => { setEditingId(d.id); setEditValue(d.display_name); }} data-testid={`dataset-rename-${d.id}`} title="Rename" className="p-2 rounded-lg border border-[#27334D] hover:border-slate-500 text-slate-400 hover:text-white transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setConfirmId(d.id)} data-testid={`dataset-delete-${d.id}`} title="Delete" className="p-2 rounded-lg border border-[#27334D] hover:border-red-500/60 text-slate-400 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}

                  {confirmId === d.id && (
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-slate-300">Delete permanently?</span>
                      <button onClick={() => doDelete(d.id)} disabled={busyId === d.id} data-testid={`dataset-delete-confirm-${d.id}`} className="flex items-center gap-1.5 text-xs bg-red-600 hover:bg-red-500 px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50">
                        {busyId === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete
                      </button>
                      <button onClick={() => setConfirmId(null)} className="text-xs px-3 py-1.5 rounded-lg border border-[#27334D] hover:border-slate-500">Cancel</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {(phase === "idle" || phase === "uploading") && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => phase === "idle" && inputRef.current?.click()}
            data-testid="csv-upload-dropzone"
            className={`rounded-2xl border-2 border-dashed p-12 text-center cursor-pointer transition-colors ${dragOver ? "border-blue-500 bg-blue-500/[0.06]" : "border-[#27334D] bg-[#111726] hover:border-slate-500"}`}
          >
            <input ref={inputRef} type="file" accept=".zip,.csv,.xlsx,.xls" className="hidden" data-testid="csv-file-input" onChange={(e) => handleFile(e.target.files[0])} />
            {phase === "uploading" ? (
              <div className="flex flex-col items-center gap-3 text-slate-300"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /> Uploading & extracting your connections…</div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-blue-600/15 border border-blue-500/25 flex items-center justify-center"><UploadCloud className="w-7 h-7 text-blue-400" /></div>
                <div className="font-medium">Drag & drop your contacts file</div>
                <div className="flex flex-wrap items-center justify-center gap-2 mt-1" data-testid="upload-formats-badges">
                  {["CSV", "Excel (.xlsx)", "ZIP"].map((f) => (
                    <span key={f} className="text-[11px] font-mono-niq uppercase tracking-wider text-blue-300 bg-blue-600/15 border border-blue-500/25 px-2.5 py-1 rounded-full">{f}</span>
                  ))}
                </div>
                <div className="text-sm text-slate-500">or click to browse · up to 60MB · we pick out your contacts automatically</div>
              </div>
            )}
          </div>
        )}

        {phase === "processing" && (
          <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-8">
            <div className="flex items-center gap-3 mb-6">
              <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
              <div className="flex-1">
                <div className="font-display text-lg font-semibold">Enriching your network</div>
                <div className="text-sm text-slate-500 font-mono-niq">{progress.enriched.toLocaleString()} of {progress.limit.toLocaleString()} contacts classified</div>
              </div>
              {etaLabel && (
                <div className="text-right shrink-0" data-testid="enrichment-eta">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-niq">Estimated</div>
                  <div className="text-sm font-semibold text-blue-300 font-mono-niq">{etaLabel}</div>
                </div>
              )}
            </div>
            <div className="h-2 rounded-full bg-[#1E2A3F] overflow-hidden mb-2"><div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${pct}%` }} /></div>
            <div className="flex justify-between text-xs text-slate-500 font-mono-niq"><span data-testid="enrichment-pct">{pct}%</span><span>{progress.total.toLocaleString()} total in file</span></div>
            {progress.locked > 0 && (
              <div className="mt-5 flex items-center gap-2 text-xs text-amber-300 bg-amber-500/[0.06] border border-amber-500/25 rounded-lg px-3 py-2">
                <Lock className="w-3.5 h-3.5" /> {progress.locked} contacts are locked on your current plan. Subscribe to Pro to enrich them.
              </div>
            )}
            <p className="text-sm text-slate-400 mt-4">You can leave this page; enrichment continues in the background.</p>
          </div>
        )}

        {phase === "done" && (
          <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.05] p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <div className="font-display text-xl font-semibold mb-1">Enrichment complete</div>
            <p className="text-slate-400 mb-6">{progress.enriched} contacts classified and ready to explore.</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => navigate("/dashboard")} data-testid="goto-dashboard-button" className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-5 py-2.5 rounded-lg font-medium text-sm transition-colors">View dashboard <ArrowRight className="w-4 h-4" /></button>
              <button onClick={() => navigate("/search")} className="flex items-center gap-2 border border-[#27334D] hover:border-slate-500 px-5 py-2.5 rounded-lg font-medium text-sm transition-colors"><FileText className="w-4 h-4" /> Ask a question</button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
